import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, where, deleteDoc, doc, Timestamp, orderBy, updateDoc } from 'firebase/firestore';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt, FaWhatsapp, FaTrashAlt, FaListUl, FaChevronRight, FaBell, FaTimes, FaCalendarCheck, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

registerLocale('es', es);

const CalendarioView = () => {
    const [fecha, setFecha] = useState(new Date());
    const [citasDelDia, setCitasDelDia] = useState([]);
    const [todasLasCitas, setTodasLasCitas] = useState([]);
    const [alertasEmitidas, setAlertasEmitidas] = useState([]);
    const [citaSeleccionada, setCitaSeleccionada] = useState(null);
    const audioRef = useRef(null);

    // 1. Efecto para traer las reservas del día seleccionado
    useEffect(() => {
        const inicio = new Date(fecha);
        inicio.setHours(0, 0, 0, 0);
        const fin = new Date(fecha);
        fin.setHours(23, 59, 59, 999);

        // Asumimos que la colección donde caen las reservas web se llama 'citas'
        const q = query(
            collection(db, 'citas'),
            where('fechaInicio', '>=', Timestamp.fromDate(inicio)),
            where('fechaInicio', '<=', Timestamp.fromDate(fin)),
            orderBy('fechaInicio', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const detalles = [];
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const d = data.fechaInicio.toDate();
                const HHmm = d.getHours().toString().padStart(2, '0') + ":" + d.getMinutes().toString().padStart(2, '0');
                detalles.push({ id: doc.id, ...data, horaFmt: HHmm, fechaObjeto: d });
            });
            setCitasDelDia(detalles);
        });
        return () => unsubscribe();
    }, [fecha]);

    // 2. Efecto para la Hoja de Ruta Global (Todas las reservas futuras)
    useEffect(() => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        const qGlobal = query(
            collection(db, 'citas'),
            where('fechaInicio', '>=', Timestamp.fromDate(hoy)),
            orderBy('fechaInicio', 'asc')
        );

        const unsubscribeGlobal = onSnapshot(qGlobal, (snapshot) => {
            const listaGlobal = snapshot.docs.map(doc => {
                const data = doc.data();
                const d = data.fechaInicio.toDate();
                return {
                    id: doc.id,
                    ...data,
                    fechaFmt: d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                    horaFmt: d.getHours().toString().padStart(2, '0') + ":" + d.getMinutes().toString().padStart(2, '0'),
                    fechaObjeto: d
                };
            });
            setTodasLasCitas(listaGlobal);
        });
        return () => unsubscribeGlobal();
    }, []);

    // 3. Sistema de Alertas
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio('/alerta.mp3');
        }

        const intervalId = setInterval(() => {
            const ahora = new Date();
            
            todasLasCitas.forEach(cita => {
                const diferenciaMs = cita.fechaObjeto.getTime() - ahora.getTime();
                const diferenciaMinutos = Math.floor(diferenciaMs / 60000);

                if (diferenciaMinutos > 0 && diferenciaMinutos <= 30 && !alertasEmitidas.includes(cita.id)) {
                    audioRef.current.play().catch(e => console.log("Audio bloqueado por el navegador"));

                    toast((t) => (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 font-black text-kokimiko-naranja text-lg border-b pb-2">
                                <FaBell className="animate-bounce" /> ¡LLEGADA EN {diferenciaMinutos} MINUTOS!
                            </div>
                            <span className="font-bold text-kokimiko-texto">{cita.nombreCliente}</span>
                            <span className="text-sm text-slate-500">Hora: {cita.horaFmt} hs</span>
                        </div>
                    ), { 
                        duration: 15000, 
                        icon: '🚨',
                        style: { border: '2px solid #E15A42', padding: '16px', borderRadius: '16px' }
                    });

                    setAlertasEmitidas(prev => [...prev, cita.id]);
                }
            });
        }, 30000);

        return () => clearInterval(intervalId);
    }, [todasLasCitas, alertasEmitidas]);

    const handleEliminarCita = async (id, e) => {
        if (e) e.stopPropagation();
        if (window.confirm(`¿Estás seguro de que quieres cancelar esta reserva?`)) {
            try {
                await deleteDoc(doc(db, 'citas', id));
                toast.success("Reserva eliminada del sistema");
                if (citaSeleccionada && citaSeleccionada.id === id) {
                    setCitaSeleccionada(null);
                }
            } catch (error) {
                toast.error("Error al procesar la cancelación");
            }
        }
    };

    const handleAprobarCita = async (id, e) => {
        if (e) e.stopPropagation();
        try {
            await updateDoc(doc(db, 'citas', id), { estado: 'Confirmada' });
            toast.success("Reserva confirmada");
            if (citaSeleccionada && citaSeleccionada.id === id) {
                setCitaSeleccionada({ ...citaSeleccionada, estado: 'Confirmada' });
            }
        } catch (error) {
            toast.error("Error al confirmar reserva");
        }
    };

    // Función para determinar el color según el tipo de evento (si no hay tipo, usa default)
    const getColorPorTipo = (tipo) => {
        const t = tipo?.toLowerCase() || '';
        if (t.includes('fiesta')) return { bg: 'bg-kokimiko-texto', text: 'text-white', border: 'border-kokimiko-texto', light: 'bg-kokimiko-texto/10', textLight: 'text-kokimiko-texto' };
        if (t.includes('taller')) return { bg: 'bg-kokimiko-teal', text: 'text-white', border: 'border-kokimiko-teal', light: 'bg-kokimiko-teal/10', textLight: 'text-kokimiko-teal' };
        if (t.includes('niño') || t.includes('hora')) return { bg: 'bg-kokimiko-naranja', text: 'text-white', border: 'border-kokimiko-naranja', light: 'bg-kokimiko-naranja/10', textLight: 'text-kokimiko-naranja' };
        return { bg: 'bg-kokimiko-verde', text: 'text-white', border: 'border-kokimiko-verde', light: 'bg-kokimiko-verde/10', textLight: 'text-kokimiko-verde' };
    };

    return (
        <div className="p-4 lg:p-8 bg-kokimiko-fondo/30 min-h-screen relative font-sans">
            <Toaster position="top-right" />
            
            <div className="max-w-7xl mx-auto space-y-8">
                
                {/* SECCIÓN 1: CALENDARIO Y AGENDA DEL DÍA */}
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* IZQUIERDA: SELECTOR DE FECHA */}
                    <div className="lg:w-1/3">
                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex flex-col items-center">
                            <h3 className="font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-2 mb-6 text-sm">
                                <FaCalendarAlt className="text-kokimiko-teal" /> Radar Kokimiko
                            </h3>
                            <DatePicker 
                                selected={fecha} 
                                onChange={d => setFecha(d)} 
                                inline locale="es" 
                            />
                        </div>
                    </div>

                    {/* DERECHA: VISOR DE RESERVAS DEL DÍA */}
                    <div className="lg:w-2/3">
                        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 min-h-full">
                            <h3 className="text-xl font-black text-kokimiko-texto mb-6 uppercase tracking-wider flex justify-between items-center">
                                Reservas del Día
                                <span className="text-sm bg-kokimiko-texto text-white px-4 py-1 rounded-full">{fecha.toLocaleDateString('es-ES')}</span>
                            </h3>
                            
                            <div className="grid grid-cols-1 gap-4">
                                {citasDelDia.length === 0 ? (
                                    <div className="p-8 text-center text-gray-400 italic font-medium bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                                        No hay reservas web programadas para este día.
                                    </div>
                                ) : (
                                    citasDelDia.map(cita => {
                                        const colores = getColorPorTipo(cita.tipo);
                                        return (
                                            <div 
                                                key={cita.id} 
                                                onClick={() => setCitaSeleccionada(cita)}
                                                className={`p-5 rounded-3xl border-2 flex items-center justify-between shadow-sm cursor-pointer transition-all hover:-translate-y-1 hover:shadow-md ${colores.border} ${colores.light}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <span className={`text-xl font-black px-4 py-2 rounded-xl ${colores.bg} ${colores.text} shadow-sm`}>
                                                        {cita.horaFmt}
                                                    </span>
                                                    <div>
                                                        <h4 className={`font-extrabold text-lg ${colores.textLight}`}>{cita.nombreCliente}</h4>
                                                        <p className="text-xs font-bold text-gray-500 uppercase">{cita.tipo || 'Evento General'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase ${cita.estado === 'Confirmada' ? 'bg-kokimiko-verde text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                        {cita.estado || 'Pendiente'}
                                                    </span>
                                                    <FaChevronRight className="text-gray-400" />
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECCIÓN 2: HOJA DE RUTA GLOBAL */}
                <div className="bg-kokimiko-texto p-8 rounded-[3rem] shadow-xl overflow-hidden relative mt-8">
                    <div className="absolute top-0 right-0 p-10 opacity-5">
                        <FaListUl size={120} className="text-white" />
                    </div>
                    
                    <div className="relative z-10">
                        <h3 className="text-2xl font-black text-white mb-8 flex items-center gap-3 uppercase tracking-wider">
                            <div className="p-3 bg-kokimiko-teal rounded-2xl">
                                <FaListUl className="text-white" />
                            </div>
                            Próximas Reservas
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {todasLasCitas.length === 0 ? (
                                <p className="text-gray-400 italic">No hay actividades programadas próximamente en el sistema.</p>
                            ) : (
                                todasLasCitas.map(cita => {
                                    const colores = getColorPorTipo(cita.tipo);
                                    return (
                                        <div 
                                            key={cita.id} 
                                            onClick={() => setCitaSeleccionada(cita)}
                                            className="bg-white/10 backdrop-blur-sm p-6 rounded-3xl border border-white/10 hover:border-kokimiko-teal transition-all group shadow-sm cursor-pointer relative overflow-hidden"
                                        >
                                            <div className={`absolute top-0 left-0 w-2 h-full ${colores.bg}`}></div>
                                            <div className="flex justify-between items-start mb-4 pl-2">
                                                <div className="flex flex-col">
                                                    <span className={`text-sm font-black px-3 py-1 rounded-lg uppercase tracking-tighter mb-2 w-fit shadow-sm ${colores.bg} ${colores.text}`}>
                                                        {cita.fechaFmt}
                                                    </span>
                                                    <span className="text-2xl font-bold text-white tracking-tighter">{cita.horaFmt} hs</span>
                                                </div>
                                                <span className={`text-[9px] font-black px-2 py-1 rounded uppercase ${cita.estado === 'Confirmada' ? 'bg-kokimiko-verde text-white' : 'bg-gray-400 text-white'}`}>
                                                    {cita.estado || 'Pendiente'}
                                                </span>
                                            </div>
                                            <h4 className="text-white font-extrabold text-base mb-1 pl-2">{cita.nombreCliente}</h4>
                                            <p className="text-gray-300 text-[11px] font-bold uppercase pl-2 mb-4">{cita.tipo || 'Evento'}</p>
                                            
                                            <div className="flex items-center justify-between pl-2 pt-4 border-t border-white/10">
                                                <button onClick={(e) => {
                                                    e.stopPropagation();
                                                    const [d, m, y] = cita.fechaFmt.split('/');
                                                    setFecha(new Date(`${y}-${m}-${d}`));
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }} className="text-[10px] font-bold text-kokimiko-teal flex items-center gap-1 hover:text-white transition-colors uppercase z-10 relative">
                                                    VER EN EL RADAR <FaChevronRight />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* MODAL DE DETALLE DE RESERVA */}
            {citaSeleccionada && (
                <div className="fixed inset-0 bg-kokimiko-texto/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full p-8 relative animate-fade-in border border-gray-100">
                        <button onClick={() => setCitaSeleccionada(null)} className="absolute top-6 right-6 text-gray-400 hover:text-kokimiko-naranja transition-colors bg-gray-50 hover:bg-red-50 p-3 rounded-full">
                            <FaTimes size={20} />
                        </button>
                        
                        <h2 className="text-2xl font-black text-kokimiko-texto mb-6 border-b border-gray-100 pb-4 flex items-center gap-3 tracking-tighter uppercase">
                            <div className={`p-3 text-white rounded-xl ${getColorPorTipo(citaSeleccionada.tipo).bg}`}>
                                <FaCalendarCheck />
                            </div>
                            Expediente de Reserva
                        </h2>
                        
                        <div className="space-y-6 text-sm text-gray-700">
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Nombre del Cliente / Niño</p>
                                <p className="text-2xl font-black text-kokimiko-texto">{citaSeleccionada.nombreCliente}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Fecha y Hora</p>
                                    <p className="font-bold text-kokimiko-teal text-lg">
                                        {citaSeleccionada.fechaFmt} <br/> {citaSeleccionada.horaFmt} hs
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Tipo de Evento</p>
                                    <p className={`font-bold text-sm uppercase ${getColorPorTipo(citaSeleccionada.tipo).textLight}`}>{citaSeleccionada.tipo || 'General'}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-kokimiko-verde/10 p-4 rounded-2xl border border-kokimiko-verde/20">
                                    <p className="text-[10px] font-bold text-kokimiko-verde uppercase tracking-widest mb-1">Teléfono</p>
                                    <p className="font-bold text-kokimiko-texto flex items-center gap-2">
                                        <FaWhatsapp className="text-kokimiko-verde" /> {citaSeleccionada.telefonoCliente || 'No registrado'}
                                    </p>
                                    {citaSeleccionada.telefonoCliente && (
                                        <a href={`https://wa.me/${citaSeleccionada.telefonoCliente}`} target="_blank" rel="noreferrer" className="text-[10px] text-kokimiko-verde font-bold mt-2 inline-block hover:underline">
                                            Enviar Mensaje
                                        </a>
                                    )}
                                </div>
                                <div className="bg-kokimiko-teal/10 p-4 rounded-2xl border border-kokimiko-teal/20">
                                    <p className="text-[10px] font-bold text-kokimiko-teal uppercase tracking-widest mb-1">Email</p>
                                    <p className="font-bold text-kokimiko-texto truncate flex items-center gap-2" title={citaSeleccionada.emailCliente}>
                                        <FaEnvelope className="text-kokimiko-teal" /> {citaSeleccionada.emailCliente || 'No registrado'}
                                    </p>
                                </div>
                            </div>

                            {citaSeleccionada.notas && (
                                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-200">
                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                                        Notas Adicionales del Cliente
                                    </p>
                                    <p className="whitespace-pre-wrap italic text-gray-600 font-medium leading-relaxed">
                                        {citaSeleccionada.notas}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-100">
                            <button onClick={(e) => handleEliminarCita(citaSeleccionada.id, e)} className="px-4 py-3 text-kokimiko-naranja hover:bg-kokimiko-naranja/10 rounded-xl font-bold transition-colors flex items-center gap-2 text-sm">
                                <FaTrashAlt /> Cancelar Reserva
                            </button>
                            {citaSeleccionada.estado !== 'Confirmada' ? (
                                <button onClick={(e) => handleAprobarCita(citaSeleccionada.id, e)} className="px-6 py-3 bg-kokimiko-verde text-white rounded-xl hover:opacity-80 font-black transition-all shadow-lg uppercase tracking-widest flex items-center gap-2">
                                    <FaCheckCircle /> Aprobar
                                </button>
                            ) : (
                                <button onClick={() => setCitaSeleccionada(null)} className="px-8 py-3 bg-kokimiko-texto text-white rounded-xl hover:opacity-90 font-black transition-all shadow-lg uppercase tracking-widest">
                                    Cerrar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarioView;